import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import SettingsIcon from '@mui/icons-material/Settings';
import { Alert, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useLogout, useRename } from '../hooks/useAuthActions';
import { useAnnouncerSetting } from '../hooks/useAnnouncer';
import { usePush } from '../hooks/usePush';
import { useCurrentHangout, useMyEntry } from '../hooks/useHangout';
import { HandleSchema } from '../schemas/auth.schema';
import { getErrorMessage } from '../services/api-client';
import { TokenBadge } from '../components/TokenBadge';
import { useAuthStore } from '../stores/authStore';
import { colors } from '../themes/theme';

export function Me() {
    const session = useAuthStore((s) => s.session);
    const hangout = useCurrentHangout();
    const entry = useMyEntry(hangout.data?.id);
    const rename = useRename();
    const logout = useLogout();

    const [handle, setHandle] = useState(session?.handle ?? '');
    const handleCheck = HandleSchema.safeParse(handle);
    const changed = handle.trim() !== session?.handle;

    return (
        <Stack spacing={3}>
            <Typography variant="h2">You</Typography>

            {entry.isSuccess && (
                <Paper sx={{ p: 2 }}>
                    <TokenBadge
                        balance={entry.data.balance}
                        eliminated={entry.data.eliminatedAt !== null}
                    />
                </Paper>
            )}

            <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: colors.muted }}>
                    Handle
                </Typography>
                <TextField
                    label="Handle"
                    value={handle}
                    onChange={(event) => setHandle(event.target.value)}
                    error={handle.length > 0 && !handleCheck.success}
                    helperText={
                        handle.length > 0 && !handleCheck.success
                            ? handleCheck.error.issues[0]?.message
                            : 'What everyone sees on the leaderboard'
                    }
                    autoComplete="off"
                />
                {rename.isError && <Alert severity="error">{getErrorMessage(rename.error)}</Alert>}
                <Button
                    variant="contained"
                    disabled={!handleCheck.success || !changed || rename.isPending}
                    onClick={() => rename.mutate(handle.trim())}
                >
                    {rename.isPending ? 'Saving…' : 'Save handle'}
                </Button>
            </Stack>

            <NotificationSetting />

            <AnnouncerSetting />

            {session?.isAdmin && (
                <Button
                    component={RouterLink}
                    to="/admin"
                    variant="outlined"
                    color="primary"
                    startIcon={<SettingsIcon />}
                >
                    Commissioner tools
                </Button>
            )}

            <Button
                component={RouterLink}
                to="/install"
                variant="outlined"
                color="inherit"
                startIcon={<AddToHomeScreenIcon />}
            >
                Add to home screen
            </Button>

            <Button
                variant="outlined"
                color="inherit"
                disabled={logout.isPending}
                onClick={() => logout.mutate()}
            >
                Sign out
            </Button>

        </Stack>
    );
}

function NotificationSetting() {
    const push = usePush();

    if (!push.supported) return null;

    return (
        <Stack spacing={1}>
            <Typography variant="overline" sx={{ color: colors.muted }}>
                Notifications
            </Typography>

            {push.needsInstall ? (
                <Alert severity="info">
                    On an iPhone, notifications only reach an installed app. Add Pony Up to your
                    home screen first.
                </Alert>
            ) : push.subscribed ? (
                <>
                    <Typography variant="body2" sx={{ color: colors.inkSoft }}>
                        You&rsquo;ll hear about new bets and settlements.
                    </Typography>
                    <Button variant="outlined" color="inherit" onClick={() => void push.disable()}>
                        Turn them off
                    </Button>
                </>
            ) : (
                <>
                    <Typography variant="body2" sx={{ color: colors.inkSoft }}>
                        Get told when a bet goes up or is about to close.
                    </Typography>
                    <Button variant="contained" onClick={() => void push.enable()}>
                        Turn them on
                    </Button>
                </>
            )}

            {push.error && <Alert severity="error">{push.error}</Alert>}
        </Stack>
    );
}

function AnnouncerSetting() {
    const announcer = useAnnouncerSetting();

    if (!announcer.supported) return null;

    return (
        <Stack spacing={1}>
            <Typography variant="overline" sx={{ color: colors.muted }}>
                Read bets out loud
            </Typography>
            <Typography variant="body2" sx={{ color: colors.inkSoft }}>
                Announces new bets, thirty-second warnings and results on this phone. Meant for
                one phone on a speaker rather than everybody&rsquo;s pocket.
            </Typography>
            <Button
                variant={announcer.enabled ? 'contained' : 'outlined'}
                color={announcer.enabled ? 'primary' : 'inherit'}
                onClick={() => announcer.setEnabled(!announcer.enabled)}
            >
                {announcer.enabled ? 'Announcing — turn it off' : 'Turn it on'}
            </Button>
        </Stack>
    );
}
